import { icons } from 'common'
import { usePriceFeed } from 'hooks/usePriceFeed'
import { useMemo } from 'react'
import { useState } from 'react'
import styled, { css } from 'styled-components'
import { currencyFormatter, throttle } from 'utils'
import { useConvertAmount } from 'utils/hooks'
import { useToken } from 'utils/hooks/useToken'

import { config } from 'config'
import { useLvgState } from 'hooks/games/lvg/useLvgState'
import { LONG_DASH } from 'constants/index'
import { useEffect } from 'react'
import ms from 'ms.macro'
import { useUser } from 'hooks/user/useUser'
import { ASSETS_TYPES } from 'constants/assets'
import { useLvgOrderManager } from 'hooks/games/lvg/useYourBidsOrders'
import { useYoloToast } from 'lib/yoloToasts/useYoloToast'
import { useDebouncedCallback } from 'utils/hooks/useDebounceCallback'
import { UpDownToggle } from 'components/Molecules/UpDownToggle'
// import { AssetsSelector } from './AssetSelector'
import { Section } from '../SharedComponents/Section'
import { PnLThreshold } from '../SharedComponents/PnLThresholds'
import { AiIndexesSection } from './AiIndexesSection'
import { MultiplierSlider } from '../SharedComponents/MultiplierSlider'
import { onlyDigits } from 'utils/uiUtils_TS'

const SIDE_MAPPING = {
  up: 'buy',
  down: 'sell',
  buy: 'up',
  sell: 'down',
}

const SLIDER_RANGE = { min: 1, max: 1000 }

const NEW_ORDER_PARAMS_INIT = {
  asset: '',
  assetType: '',
  side: 'buy',
  amount: 0,
  leverage: 1,
  takeProfitPrice: '',
  takeProfitValue: '',
  userStopLossPrice: '',
  userStopLossValue: '',
}

function logSlider(position, leverageRange) {
  const minP = SLIDER_RANGE.min
  const maxP = SLIDER_RANGE.max

  const minV = Math.log(leverageRange.min)
  const maxV = Math.log(leverageRange.max)

  const scale = (maxV - minV) / (maxP - minP)

  if (+position === maxP) return leverageRange.max

  return Math.exp(minV + scale * (position - minP))
}

function logPosition(value, leverageRange) {
  const minP = Number(SLIDER_RANGE.min)
  const maxP = Number(SLIDER_RANGE.max)

  const minV = Math.log(leverageRange.min)
  const maxV = Math.log(leverageRange.max)

  const scale = (maxV - minV) / (maxP - minP)
  return (Math.log(value) - minV) / scale + minP
}

export const AutoGptCard = ({ game }) => {
  const { yToast } = useYoloToast()
  const { activeAsset, setLiveOrder } = useLvgState()
  const { account } = useUser('wallet')

  const [newOrderParams, setNewOrderParams] = useState(NEW_ORDER_PARAMS_INIT)
  const updateNewOrderParam = (paramValueObj) => setNewOrderParams((prev) => ({ ...prev, ...paramValueObj }))

  const [sliderPosition, setSliderPosition] = useState(1)
  const [placeBidError, setPlaceBidError] = useState(false)

  const [takeProfitPricePh, setTakeProfitPricePh] = useState('')
  const [takeProfitValuePh, setTakeProfitValuePh] = useState('')

  const [userStopLossPricePh, setUserStopLossPricePh] = useState('')
  const [userStopLossValuePh, setUserStopLossValuePh] = useState('')

  useEffect(() => {
    updateNewOrderParam({
      asset: activeAsset.id,
      assetType: activeAsset.type === ASSETS_TYPES.MEME_COIN ? ASSETS_TYPES.CRYPTO : activeAsset.type,
    })
  }, [activeAsset])

  const checkBidAmountOutRange = () => {
    const isAboveMaxBid = Number(newOrderParams.amount) > activeAsset.maxBidAmount && newOrderParams.amount !== ''
    const isBelowMinLvg =
      Number(newOrderParams.amount) * Number(newOrderParams.leverage) < activeAsset.minLeverageAmount &&
      newOrderParams.amount !== ''
    const isAboveMaxLvg =
      Number(newOrderParams.amount) * Number(newOrderParams.leverage) > activeAsset.maxLeverageAmount &&
      newOrderParams.amount !== ''
    return isBelowMinLvg
      ? 'minLvgAmountError'
      : isAboveMaxBid
      ? 'maxBidError'
      : isAboveMaxLvg
      ? 'maxLvgAmountError'
      : false
  }

  const checkBidOrderErrors = () => {
    const isWrongSide = activeAsset.bidSideConstrain === newOrderParams.side
    if (isWrongSide) {
      setPlaceBidError(`Current asset is not shortable`)
      return true
    }

    const isAboveMaxBid = Number(newOrderParams.amount) > activeAsset.maxBidAmount && newOrderParams.amount !== ''
    if (isAboveMaxBid) {
      setPlaceBidError(
        `Your bid must be ${currencyFormatter(activeAsset.maxBidAmount, {
          noCurrencySign: true,
        })} or lower`
      )
      return true
    }

    const isBelowMinLvg =
      Number(newOrderParams.amount) * Number(newOrderParams.leverage) < activeAsset.minLeverageAmount &&
      newOrderParams.amount !== ''
    if (isBelowMinLvg) {
      setPlaceBidError(
        `Your bid must be ${currencyFormatter(activeAsset.minLeverageAmount, {
          noCurrencySign: true,
        })} or greater`
      )
      return true
    }

    const isAboveMaxLvg =
      Number(newOrderParams.amount) * Number(newOrderParams.leverage) > activeAsset.maxLeverageAmount &&
      newOrderParams.amount !== ''
    if (isAboveMaxLvg) {
      setPlaceBidError(
        `Your leverage bid must be ${currencyFormatter(activeAsset.maxLeverageAmount, {
          noCurrencySign: true,
        })} or lower`
      )
      return true
    }

    return false
  }

  const { DEFAULT_FIAT } = config

  const { priceFeed } = usePriceFeed(activeAsset?.id)

  const { tokenId, formatToken } = useToken()
  const { yoloWalletAmount, tokenAmount } = useUser('balance')
  const convert = useConvertAmount()

  const { sendOrder } = useLvgOrderManager()

  const calculateBustPrice = (side, lvg, entryPrice) => {
    const maxLvg = activeAsset.leverageRange?.max
    const alpha = activeAsset.id === 'PEPE/USD' ? 0.83 : 0.6
    const a = (1 + alpha) ** (1 / maxLvg)

    let bustPrice
    if (side === 'buy') {
      bustPrice = entryPrice * (1 - (2 - a ** lvg) / lvg)
      bustPrice = Math.max(0.15 * entryPrice, bustPrice)
    } else bustPrice = entryPrice * (1 + (2 - a ** lvg) / lvg)

    return bustPrice
  }

  const balance = useMemo(() => {
    const fiatTokenBalance =
      convert(formatToken(yoloWalletAmount), tokenId, DEFAULT_FIAT, {
        number: true,
      }) || 0
    const fiatWalletBalance =
      convert(formatToken(tokenAmount), tokenId, DEFAULT_FIAT, {
        number: true,
      }) || 0
    const totalBiddableBalance = fiatTokenBalance + fiatWalletBalance
    return currencyFormatter(totalBiddableBalance)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [DEFAULT_FIAT, yoloWalletAmount, tokenAmount, tokenId])

  const bustPrice = useMemo(() => {
    if (!newOrderParams.amount) return 0
    const bustPrice = calculateBustPrice(newOrderParams.side, newOrderParams.leverage, priceFeed?.price)
    return bustPrice
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newOrderParams?.amount, priceFeed?.price, newOrderParams?.leverage, newOrderParams.side, activeAsset])

  //PEPE/USD leverage warning
  const debouncedYToast = useDebouncedCallback(yToast, 1000)
  useEffect(() => {
    if (activeAsset?.id === 'PEPE/USD' || (activeAsset?.id === 'WOJAK/USD' && newOrderParams.leverage > 10)) {
      const PEPE_WARNING_TOAST_OBJ = {
        id: 'warningToast',
        props: {
          message: {
            title: 'Leverage Warning',
            subtitle: `Leverage 10x or above is not recommended for $${activeAsset?.id.split('/')[0]}`,
          },
        },
      }

      debouncedYToast(PEPE_WARNING_TOAST_OBJ)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newOrderParams?.leverage, activeAsset?.id])

  useEffect(() => {
    let timeout
    const bidAmountErrorRange = checkBidAmountOutRange()
    if (bustPrice && !bidAmountErrorRange) {
      setLiveOrder({
        asset: activeAsset,
        bustPrice,
        side: SIDE_MAPPING[newOrderParams.side],
      })
    } else setLiveOrder({})

    timeout = setTimeout(() => setLiveOrder({}), ms`3s`)
    return () => {
      clearTimeout(timeout)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newOrderParams?.leverage, newOrderParams?.amount])

  useEffect(() => {
    let timeout
    timeout = setTimeout(() => setPlaceBidError(false), ms`3s`)
    return () => {
      clearTimeout(timeout)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placeBidError])

  const onSideToggle = (checked) => {
    if (!checked) updateNewOrderParam({ side: 'buy' })
    else updateNewOrderParam({ side: 'sell' })
  }

  const onSendOrderClickRaw = () => {
    const order = {
      asset: newOrderParams.asset,
      side: newOrderParams.side,
      amount: Number(newOrderParams.amount),
      assetType: newOrderParams.assetType,
      leverage: newOrderParams.leverage,
      takeProfitPrice: newOrderParams.takeProfitPrice,
      takeProfitValue: newOrderParams.takeProfitValue,
      userStopLossPrice: newOrderParams.userStopLossPrice,
      userStopLossValue: newOrderParams.userStopLossValue ? `-${newOrderParams.userStopLossValue}` : '',
      trailingStopValue: newOrderParams.trailingStopValue ? Number(newOrderParams.trailingStopValue) : undefined,
      trailingStopPercent: newOrderParams.trailingStopPercent ? Number(newOrderParams.trailingStopPercent) : undefined,
    }
    const orderHasError = checkBidOrderErrors()
    if (orderHasError) return

    sendOrder(order)
  }

  const onSendOrderClick = throttle(onSendOrderClickRaw, ms`1s`)

  const onOutputChange = (value) => {
    const valueN = Number(value)
    if (valueN < Number(activeAsset.leverageRange.min)) updateNewOrderParam({ leverage: valueN })
    else if (valueN > Number(activeAsset.leverageRange.max))
      updateNewOrderParam({ leverage: activeAsset.leverageRange.max })
    else updateNewOrderParam({ leverage: valueN })
  }

  const onOutputBlur = () => {
    if (newOrderParams.leverage < Number(activeAsset.leverageRange.min)) {
      updateNewOrderParam({ leverage: activeAsset.leverageRange.min })
      setSliderPosition(SLIDER_RANGE.min)
    }
  }

  const onSliderChange = (leverage) => {
    updateNewOrderParam({ leverage })
  }

  const toggleThresholds = () => {
    updateNewOrderParam({
      takeProfitPrice: '',
      takeProfitValue: '',
      userStopLossPrice: '',
      userStopLossValue: '',
    })
    setTakeProfitPricePh('')
    setTakeProfitValuePh('')

    setUserStopLossPricePh('')
    setUserStopLossValuePh('')
  }
  useEffect(toggleThresholds, [activeAsset])

  useEffect(() => {
    updateNewOrderParam({ leverage: activeAsset?.leverageRange?.min || 1 })
  }, [activeAsset?.type, activeAsset?.leverageRange?.min])

  useEffect(() => {
    if (!newOrderParams.amount) return
    const currentPrice = priceFeed?.price
    if (takeProfitPricePh) {
      const tpPricePh = currencyFormatter(Number(newOrderParams.takeProfitValue) + currentPrice, {
        noCurrencySign: false,
        decimalDigits: activeAsset.fiatDecimals,
      })
      setTakeProfitPricePh(tpPricePh)
    }
    if (takeProfitValuePh) {
      const tpValuePh = currencyFormatter(
        (newOrderParams.amount * newOrderParams.leverage * (Number(newOrderParams.takeProfitPrice) - currentPrice)) /
          currentPrice,
        {
          noCurrencySign: false,
          decimalDigits: activeAsset.fiatDecimals,
        }
      )
      setTakeProfitValuePh(tpValuePh)
    }
    if (userStopLossValuePh) {
      const slValuePh = currencyFormatter(
        (newOrderParams.amount * newOrderParams.leverage * (Number(newOrderParams.userStopLossPrice) - currentPrice)) /
          currentPrice,
        {
          noCurrencySign: false,
          decimalDigits: activeAsset.fiatDecimals,
        }
      )
      setUserStopLossValuePh(slValuePh)
    }
    if (userStopLossPricePh) {
      const slPricePh = currencyFormatter(currentPrice - Number(newOrderParams.userStopLossValue), {
        noCurrencySign: false,
        decimalDigits: activeAsset.fiatDecimals,
      })
      setUserStopLossPricePh(slPricePh)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    priceFeed?.price,
    activeAsset?.fiatDecimals,
    newOrderParams?.takeProfitValue,
    newOrderParams?.takeProfitPrice,
    newOrderParams?.userStopLossPrice,
    newOrderParams?.userStopLossValue,
  ])

  return (
    <InfiniteContainer>
      <Section noWrapper>
        <AiIndexesSection />
      </Section>
      <Section title='bid direction'>
        <UpDownToggle game={game} onToggle={onSideToggle} />
      </Section>
      <Section title='bid amount'>
        <BidAmountFiled>
          <BidInput
            type='dial'
            placeholder='0.00'
            value={newOrderParams.amount}
            onChange={(e) => updateNewOrderParam({ amount: onlyDigits(e.target.value) })}
          />
        </BidAmountFiled>
      </Section>
      <Section title='multiplier'>
        <div style={{ width: '100%' }}>
          <MultiplierSlider leverage={newOrderParams.leverage} setLeverage={onSliderChange} />
          <BustContainer>
            <MultiplyIcon>X</MultiplyIcon>
            <Output
              id='dial'
              value={newOrderParams.leverage}
              onChange={(e) => onOutputChange(onlyDigits(e.target.value))}
              onBlur={onOutputBlur}
            />
            <BreakAmount>
              <div>Bust</div>
              <div className='strong'>
                {newOrderParams.leverage === '0'
                  ? LONG_DASH
                  : currencyFormatter(bustPrice, {
                      decimalDigits: activeAsset?.fiatDecimals,
                    })}
              </div>
            </BreakAmount>
          </BustContainer>
        </div>
      </Section>
      <Section noWrapper>
        <PnLThreshold
          game={game}
          newOrderParams={newOrderParams}
          onToggle={toggleThresholds}
          onParamChange={updateNewOrderParam}
          onError={setPlaceBidError}
        />
      </Section>
      <Section>
        <div style={{ width: '100%' }}>
          <BalanceContainer>
            <BalanceTitle>Biddable Balance</BalanceTitle>
            <BalanceAmount>{balance}</BalanceAmount>
          </BalanceContainer>
          <BiddingButtonContainer>
            <TotalWithLeverage>
              <div className='subtitle'>
                Total with Leverage
                <span>{`(Max ${currencyFormatter(activeAsset?.maxLeverageAmount, {
                  decimalDigits: 0,
                })})`}</span>
              </div>
              <div className='value'>
                {currencyFormatter(Number(newOrderParams.amount) * Number(newOrderParams.leverage))}
              </div>
            </TotalWithLeverage>
            <BidButtonWrapper>
              <BackgroundLayer />
              <BidButton
                disabled={!account || !newOrderParams.amount}
                className={`bidInput ${placeBidError ? 'error' : ''}`}
                onClick={onSendOrderClick}
              >
                Place bid
              </BidButton>
            </BidButtonWrapper>
            {account && <ErrorMsg className={`input_error ${placeBidError ? '' : 'hide'}`}>{placeBidError}</ErrorMsg>}
          </BiddingButtonContainer>
        </div>
      </Section>
    </InfiniteContainer>
  )
}

const designerAssetCSS = css`
  .current_price_display {
    border-radius: 10px;
    line-height: 100%;
    height: fit-content;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    background: transparent;
    padding: 0 0 0 0;
  }

  .current_price_display label {
    font-size: 1rem;
    font-weight: 500;
    letter-spacing: -0.02em;
  }

  .current_price_display .price_direction_icon {
    mask: url(${icons.bid_direction_icon}) center center / auto 14px no-repeat;
    width: 14px;
    height: 14px;
    margin: 0 5px 0 0;
  }

  .current_price_display.up label {
    color: hsl(126, 100%, 38%);
  }

  .current_price_display .price_direction_icon.up {
    background: hsl(126, 100%, 38%);
    transform: rotate(-180deg);
  }

  .current_price_display.down label {
    color: hsl(340, 88%, 46%);
  }

  .current_price_display .price_direction_icon.down {
    background: hsl(340, 88%, 46%);
    transform: rotate(0deg);
  }

  .current_price_display.neutral {
    background: hsl(219, 29%, 15%);
  }

  .current_price_display.neutral label {
    color: hsl(221, 73%, 47%);
  }

  .current_price_display .price_direction_icon.neutral {
    background: hsl(221, 73%, 47%);
    -webkit-mask: url('../../resources/images/icons/bid_direction_neutral_icon.svg') center center / auto 12px no-repeat;
    mask: url(${icons.bid_direction_neutral_icon}) center center / auto 14px no-repeat;
    width: 14px;
    height: 14px;
    margin: 0 5px 0 0;
  }
`

const BidAmountFiled = styled.div`
  margin: 0 0 5px 0;
  display: flex;
  flex-direction: column;

  & .bidInput {
    width: 100%;
    background: hsl(218, 23%, 25%);
    -webkit-box-shadow: 0 0 40px 0 rgba(0, 0, 0, 0.3);
    box-shadow: 0 0 40px 0 rgba(0, 0, 0, 0.3);
    border-radius: 10px;
    font-size: 1.5rem;
    font-weight: 300;
    text-align: center;
    padding: 10px;
    position: relative;
    z-index: 1;
  }
`

const BackgroundLayer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: var(--primary-button-hover-background);
  border-radius: 10px;
`

const BidButton = styled.button`
  background: var(--primary-button-background);
  border-radius: 15px;
  text-align: center;
  width: 100%;
  border-radius: 10px;
  border: 1px solid var(--primary-button-border);
  display: flex;
  padding: 16px 20px;
  line-height: 100%;
  font-size: 0.9rem;
  font-weight: 600;
  z-index: 2;
  position: relative;
  justify-content: center;
  text-transform: uppercase;

  &:hover {
    background: var(--primary-button-hover-background);
  }
  &:disabled {
    cursor: not-allowed !important;
    filter: none !important;
    box-shadow: none !important;
    background: var(--primary-button-hover-background);
    border: 1px solid var(--primary-button-hover-background);
  }
`
// const BidButtonWrapper = styled.div``

const BiddingButtonContainer = styled.div`
  position: relative;
  width: 100%;
  padding: 20px 0;
`
const BidButtonWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  margin: -10px 0 0px 0;
`

const ErrorMsg = styled.div`
  padding: 15px 5px 5px 5px;
  text-align: center;
  background: hsla(360, 100%, 16%, 1);
  border-radius: 0 0 10px 10px;
  line-height: 120%;
  position: relative;
  z-index: -1;
  font-size: 0.75rem;
  transform: translateY(-10px);
  transition: all 500ms ease;
  width: 100%;

  &.hide {
    transform: translateY(-100%);
  }
`

const BalanceTitle = styled.span`
  cursor: default;
  text-transform: uppercase;
`
const BalanceAmount = styled.span`
  cursor: pointer;
  font-weight: 600;
`

const BalanceContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  font-size: 0.8rem;
  padding: 0 0 20px 0;
`

const BreakAmount = styled.div`
  width: 40%;
  padding: 0 0 0 20px;
  align-items: flex-start;
  flex-flow: column;
  justify-content: center;
  font-size: 0.7rem;

  & .strong {
    font-weight: 500;
    font-size: 0.85rem;
    padding-top: 5px;
  }
`

const Output = styled.input`
  background: hsla(0, 0%, 100%, 0.1);
  -webkit-box-shadow: 0 0 40px 0 rgb(0 0 0 / 30%);
  box-shadow: none;
  padding: 10px 16px;
  border-radius: 5px;
  font-size: 1rem;
  font-weight: 400;
  width: 60%;
  text-align: center;
  justify-content: center;
  border: 0;
  outline: 0;
`

const MultiplyIcon = styled.div`
  position: absolute;
  top: calc(50% + 1px);
  transform: translateY(-50%);
  left: 40px;
  content: '';
  display: block;
  font-weight: 700;
  font-size: 0.9rem;
  line-height: 100%;
`

const BustContainer = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  flex-flow: row;
  justify-content: space-between;
  margin: 0;
`

const SliderContainer = styled.div`
  /* padding-top: 15px; */
  padding-bottom: 0;
  position: relative;
  z-index: 1;
  width: 100%;
`
const Slider = styled.input`
  -webkit-appearance: none;
  width: 100%;
  height: 7px;
  cursor: pointer;
  background: hsla(0, 0%, 100%, 0.2);
  background-image: var(--primary-button-background);

  border-radius: 5px;
  background-size: ${({ value }) => `${(value * 100) / SLIDER_RANGE.max}%`} 100%;
  background-repeat: no-repeat;
  transition: none !important;

  ::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 20px;
    height: 20px;
    background: #fff;
    border-radius: 50%;
  }

  ::-moz-range-thumb {
    width: 20px;
    height: 20px;
    background: #fff;
    border-radius: 50%;
  }
`

const SliderLabels = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 15px;
  position: relative;
  z-index: 0;
`

const RedLabelContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: center;
`

const GreenLabelContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
`

const SliderLabelGreen = styled.label`
  font-size: 0.7rem;
  padding: 20px 0 0 0;
  margin: -10px 0 0 0;
  position: relative;
`
const SliderLabelRed = styled.label`
  font-size: 0.7rem;
  padding: 20px 0 0 0;
  margin: -10px 0 0 0;
  position: relative;
`

const InfiniteContainer = styled.div`
  height: 100%;
  width: 100%;
  padding: 0;
  opacity: 1;
  /* padding: 15px 20px; */

  /* & section:nth-child(1) {
    padding-top: 0;
  } */
  & section:last-child {
    border-bottom: none;
  }
`

const SelectContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  /* justify-content: space-between; */
  width: 100%;

  ${designerAssetCSS}
`

const BidInput = styled.input`
  width: 100%;
  -webkit-box-shadow: 0 0 40px 0 rgb(0 0 0 / 30%);
  box-shadow: 0 0 40px 0 rgb(0 0 0 / 30%);
  border-radius: 10px;
  font-size: 1.5rem;
  font-weight: 300;
  text-align: center;
  padding: 10px;
  border: 0;
  margin: 0 0 5px 0;
  background: hsla(0, 0%, 100%, 0.2); //var(--input-background);
  -webkit-box-shadow: none;
  box-shadow: none;

  &:hover,
  &:focus {
    border: 0;
    outline: 0;
  }
`

const TotalWithLeverage = styled.div`
  position: relative;
  left: 50%;
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  font-size: 0.8rem;
  background: hsla(0, 0%, 0%, 0.3);
  border-radius: 10px 10px 0 0;
  transform: translate(-50%);
  padding: 15px 15px 25px 15px;
  .subtitle {
    text-transform: uppercase;
    white-space: nowrap;
    font-size: 0.8rem;
    padding-top: 5px;
    display: flex;
    flex-direction: column;
    font-weight: 800;
  }
  .subtitle span {
    font-size: 0.7rem;
    margin: 5px 0 0 0;
    font-weight: 400;
  }
  .value {
    font-size: 1rem;
  }
`
